import React, { useEffect, useMemo, useState, memo, useCallback } from "react";
import { Image, Text, TouchableOpacity, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import ImageComponent from "../../../../Components/ImageComponent";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppColors } from "../../../../utility/AppColors";
import TimerComponent from "../../../../Components/ongoing-common-components/general-components/TimerComponent";
import { AppImages } from "../../../../utility/AppImages";
import { CommonButton } from "../../../../common/CommonButton";
import CreateEventTooltip from "../../../../Components/home-common-components/CreateEventTooltip";
import SaveChangesTooltip from "../UpComingEvents/SaveChangesTooltip";
import { format } from "date-fns";
import GameShowCardSkeleton from "../../../../Components/skeleton-components/GameShowCardSkeleton";
import RewardWagerSkeleton from "../../../../Components/skeleton-components/RewardWagerSkeleton";
import GameShowDetailSkeleton from "../../../../Components/skeleton-components/GameShowDetailSkeleton";
import PaidStreamingTimerComponent from "./PaidStreamingTimerComponent";

import { EventDetails, TimeLeft, UserData } from "./types/event.types";
import { formatStakes, isValidDate } from "./utils/eventUtils";

interface EventDetailCardProps {
  setIsMinAttendees: (value: boolean) => void;
  details: EventDetails | null;
  isMinAttendees: boolean;
  saveBtnTooltip?: boolean;
  setSaveBtnTooltip?: (value: boolean) => void;
  userData: UserData | null;
  timeLeft: TimeLeft;
  setTimeLeft: (time: TimeLeft) => void;
  setSelectedButton?: (value: string) => void;
  isMoreTimeLeft: boolean;
  setIsMoreTimeLeft: (value: boolean) => void;
  screenName?: string;
  hasCheckedIn?: boolean;
  IsEventCanceled: boolean;
  IsEventCompleted?: boolean;
  isLoading?: boolean;
  hideTimer?: boolean;
  isPastEvent?: boolean;
}

const EventDetailCard = memo<EventDetailCardProps>(
  ({
    setIsMinAttendees,
    details,
    isMinAttendees,
    saveBtnTooltip,
    setSaveBtnTooltip,
    timeLeft,
    setTimeLeft,
    isMoreTimeLeft,
    setIsMoreTimeLeft,
    screenName,
    IsEventCanceled,
    IsEventCompleted,
    isLoading = false,
    hideTimer = false,
    isPastEvent = false,
  }) => {
    const styles = useStyles();
    const deviceSize = useDeviceSize();

    const attendeeInfo = useMemo(() => {
      const signUpNumber = 70;
      const requiredAttendees = 100;
      const requiredNumber = details?.minimumAttendees
        ? Math.max(details.minimumAttendees - signUpNumber, 0)
        : 0;
      const totalPercentage = details?.minimumAttendees
        ? (signUpNumber * 100) / details.minimumAttendees
        : 0;
      const totalAttendees = signUpNumber || 0;

      return {
        signUpNumber,
        requiredNumber,
        requiredAttendees,
        totalPercentage,
        totalAttendees,
      };
    }, [details?.minimumAttendees]);

    const calculateTimeBasedValues = useCallback(() => {
      if (!details?.eventStartTime || !isValidDate(details.eventStartTime)) {
        return;
      }

      const eventDate = new Date(details.eventStartTime);
      const now = new Date();
      const timeDifference = eventDate.getTime() - now.getTime();
      const hoursLeft = timeDifference / (1000 * 60 * 60);

      if (timeDifference > 0) {
        setTimeLeft({
          days: Math.floor(timeDifference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((timeDifference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((timeDifference / 1000 / 60) % 60),
          seconds: Math.floor((timeDifference / 1000) % 60),
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }

      setIsMinAttendees(
        attendeeInfo.requiredNumber > attendeeInfo.signUpNumber
      );

      setIsMoreTimeLeft(hoursLeft > 1 || hoursLeft < 0);
    }, [
      details?.eventStartTime,
      setTimeLeft,
      setIsMinAttendees,
      setIsMoreTimeLeft,
      attendeeInfo,
    ]);

    useEffect(() => {
      calculateTimeBasedValues();

      const interval = setInterval(calculateTimeBasedValues, 1000);
      return () => clearInterval(interval);
    }, [calculateTimeBasedValues]);

    const formattedStake = useMemo(() => {
      return formatStakes(details?.stakes || []);
    }, [details?.stakes]);

    const medalsData = useMemo(
      () => [
        { id: 0, name: "Coin", image: AppImages.icCoin, value: 0 },
        { id: 1, name: "Bronze", image: AppImages.icBronzeBelt, value: 0 },
        { id: 2, name: "Silver", image: AppImages.icSilverBelt, value: 0 },
        { id: 3, name: "Gold", image: AppImages.icGoldBelt, value: 0 },
        { id: 4, name: "Platinum", image: AppImages.icPlatinumBelt, value: 0 },
      ],
      []
    );

    const updatedMedals = useMemo(() => {
      return medalsData
        .map((medal) => {
          const stake = details?.stakes?.find(
            (stake) => stake.type === medal.name
          );
          return {
            ...medal,
            value: stake ? stake.quantity : 0,
          };
        })
        .filter((medal) => medal.value > 0);
    }, [medalsData, details?.stakes]);

    const eventDate = useMemo(() => {
      return details?.eventStartTime && isValidDate(details.eventStartTime)
        ? new Date(details.eventStartTime)
        : new Date();
    }, [details?.eventStartTime]);

    const formattedDate = useMemo(() => {
      return details?.eventStartTime && isValidDate(details.eventStartTime)
        ? format(eventDate, "EEEE, MMMM d, yyyy, 'at' h:mm")
        : "";
    }, [details?.eventStartTime, eventDate]);

    const isMobilePastEvent = isPastEvent && (deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM);

    const containerStyle = [
      styles.gameshowOuterView,
      styles.deviceflexDirection,
      {
        marginTop: (hideTimer || isMobilePastEvent) ? 0 : 15,
        position: "relative" as const,
        minHeight: deviceSize === DEVICE_SIZES.XL ? 284 : undefined,
        padding: isMobilePastEvent ? 8 : (hideTimer ? 12 : 16),
        paddingHorizontal: hideTimer ? 12 : isMobilePastEvent ? 8 : 17,
        columnGap: 10,
        zIndex: -1,
        borderRadius: hideTimer ? 0 : 8,
        borderWidth: hideTimer ? 0 : 0.8,
        borderColor: hideTimer ? "transparent" : "#374151",
        flexShrink: 0,
      },
    ];

    const content = (
      <>
        {!hideTimer && (isLoading ? (
          <GameShowCardSkeleton />
        ) : details?.eventType === "Executive" ? (
          <PaidStreamingTimerComponent
            isMoreTimeLeft={isMoreTimeLeft}
            isMinAttendees={isMinAttendees}
            timeLeft={timeLeft}
            requiredAttendees={attendeeInfo.requiredAttendees}
            totalAttendees={attendeeInfo.totalAttendees}
            showTimer={false}
            formattedDate={formattedDate}
            title={details?.eventTitle || ""}
            type="EventDetail"
            IsEventCanceled={IsEventCanceled}
            IsEventCompleted={IsEventCompleted}
          />
        ) : (
          <LinearGradient
            colors={["#1F2937", "#111827"]}
            start={[0, 0]}
            end={[0, 1]}
            style={{
              justifyContent: "flex-start",
              borderRadius: 8,
              borderWidth: 0.8,
              borderColor: "#374151",
              width:
                deviceSize === DEVICE_SIZES.SM ||
                  deviceSize === DEVICE_SIZES.XS
                  ? "100%"
                  : 437,
              padding: isMobilePastEvent ? 16 : (deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM) ? 8 : 24,
              flexShrink: 0,
            }}
          >
            <View
              style={{
                width: "100%",
                gap: 16,
              }}
            >
              <TimerComponent
                isMoreTimeLeft={isMoreTimeLeft}
                isMinAttendees={isMinAttendees}
                timeLeft={timeLeft}
                requiredNumber={attendeeInfo.requiredNumber}
                totalPercentage={attendeeInfo.totalPercentage}
                showTimer={false}
                formattedDate={formattedDate}
                title={details?.eventTitle || ""}
                type="EventDetail"
                IsEventCanceled={IsEventCanceled}
                IsEventCompleted={IsEventCompleted || isPastEvent}
                pastEvent={isPastEvent}
              />
            </View>
          </LinearGradient>
        ))}
        {isLoading ? (
          <View style={[styles.eventDetailsContainer, {}]}>
            <GameShowDetailSkeleton />
          </View>
        ) : (
          <View
            style={{
              flexDirection: "row",
              flex: 1,
              gap: 20,
              justifyContent: isMobilePastEvent ? "flex-start" : "center",
              alignItems: isMobilePastEvent ? "flex-start" : "center",
              marginTop: isMobilePastEvent ? 20 : 0,
            }}
          >
            {/* KO Logo with Event Type and Game Type */}
            <View style={{ justifyContent: "center", alignItems: isMobilePastEvent ? "flex-start" : "center" }}>
              <Image
                source={AppImages.KO_LOGO_Main}
                style={{
                  width: isMobilePastEvent ? 111 : (hideTimer ? 111 : 180),
                  height: isMobilePastEvent ? 60 : (hideTimer ? 60 : 120),
                  resizeMode: "contain",
                }}
              />
              <View style={{ flexDirection: "row", gap: hideTimer ? 16 : 24, marginTop: hideTimer ? 8 : 16 }}>
                <View>
                  <Text
                    style={[
                      isMobilePastEvent ? AppFontStyle.REGULAR_ANYBODY_14 : AppFontStyle.REGULAR_ANYBODY_12,
                      { color: "#9CA3AF" },
                    ]}
                  >
                    Event type
                  </Text>
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_14,
                      { color: AppColors.white, marginTop: 4 },
                    ]}
                  >
                    {details?.type === "Private" ? "Exclusive" : "Kall Out"}
                  </Text>
                </View>
                <View>
                  <Text
                    style={[
                      isMobilePastEvent ? AppFontStyle.REGULAR_ANYBODY_14 : AppFontStyle.REGULAR_ANYBODY_12,
                      { color: "#9CA3AF" },
                    ]}
                  >
                    Game type
                  </Text>
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_14,
                      { color: AppColors.white, marginTop: 4 },
                    ]}
                  >
                    {details?.challengeMode ? "1 vs 1" : "1 vs 1"}
                  </Text>
                </View>
              </View>
            </View>

            {/* Game Image with Game Title */}
            <View style={{ alignItems: "center", flex: 1 }}>
              <ImageComponent
                url={details?.gameImage}
                style={{
                  width: isMobilePastEvent ? 100 : (hideTimer ? "100%" : 149),
                  height: isMobilePastEvent ? 58 : (hideTimer ? 58 : 149),
                  borderRadius: 8,
                  resizeMode: "cover",
                }}
              />
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_12,
                  { color: "#9CA3AF", marginTop: hideTimer ? 4 : 8 },
                ]}
              >
                Game
              </Text>
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_14,
                  { color: AppColors.white, marginTop: 2 },
                ]}
              >
                {details?.gameTitle || ""}
              </Text>
            </View>
          </View>
        )}

        {isLoading ? (
          <RewardWagerSkeleton />
        ) : (
          <View
            style={{
              justifyContent: "space-between",
              marginTop: isMobilePastEvent ? 10 : 0,
            }}
          >
            {screenName === "GameShowScreen" ? (
              <View style={{ alignItems: "center", zIndex: -1 }}>
                <View
                  style={{
                    width:
                      deviceSize === DEVICE_SIZES.XS ||
                        deviceSize === DEVICE_SIZES.SM
                        ? "100%"
                        : 212,
                    alignItems: "center",
                  }}
                >
                  <Text style={AppFontStyle.MEDIUM_ANYBODY_12}>
                    This challenge is for 2 Bronze Belts:
                    <Text style={AppFontStyle.BOLD_ANYBODY_16}>
                      worth 120 credits
                    </Text>
                  </Text>
                </View>
              </View>
            ) : (
              <>
                <View style={{ position: "relative", width: "100%" }}>
                  {saveBtnTooltip && (
                    <View style={{ position: "relative", zIndex: 9999 }}>
                      <CreateEventTooltip
                        content={
                          <SaveChangesTooltip visible={saveBtnTooltip} />
                        }
                        width={
                          deviceSize === DEVICE_SIZES.XS ||
                            deviceSize === DEVICE_SIZES.SM
                            ? 350
                            : 443
                        }
                        maxHeight={302}
                        visible={saveBtnTooltip}
                        setVisible={setSaveBtnTooltip}
                        closeImage={AppImages.icCloseWhiteSvg}
                        boxViewStyle={{
                          borderWidth: 0,
                          padding: 0,
                        }}
                        right={
                          deviceSize === DEVICE_SIZES.XS ||
                            deviceSize === DEVICE_SIZES.SM
                            ? -5
                            : 40
                        }
                        bottom={
                          deviceSize === DEVICE_SIZES.XS ||
                            deviceSize === DEVICE_SIZES.SM
                            ? 0
                            : -263
                        }
                      />
                    </View>
                  )}
                </View>
                {!isMobilePastEvent && !hideTimer && (
                  <View style={{ alignItems: "center", zIndex: -1 }}>
                    <View
                      style={{
                        width:
                          deviceSize === DEVICE_SIZES.XS ||
                            deviceSize === DEVICE_SIZES.SM
                            ? "100%"
                            : 212,
                        alignItems: "center",
                      }}
                    >
                      <Text
                        style={{
                          color: "#FFF",
                          fontSize: hideTimer ? 12 : undefined,
                          fontWeight: hideTimer ? "500" : undefined,
                          lineHeight: hideTimer ? 13.86 : undefined,
                          ...(!hideTimer && AppFontStyle.MEDIUM_ANYBODY_12),
                        }}
                      >
                        You have been challenged to {formattedStake} Belts{" "}
                        <Text
                          style={{
                            color: "#FFF",
                            fontSize: hideTimer ? 14 : 16,
                            fontWeight: "700",
                            lineHeight: hideTimer ? 16.17 : undefined,
                          }}
                        >
                          worth {details?.totalCredits} credits
                        </Text>
                      </Text>
                    </View>
                  </View>
                )}
              </>
            )}


            {!isMobilePastEvent && !(deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM) && (
              <View
                style={{
                  width: 212,
                  alignItems: "center",
                  zIndex: -1,
                  marginTop: 20,
                  marginBottom: 10,
                }}
              >
                <LinearGradient
                  colors={["#1F2937", "#111827"]}
                  start={[0, 0]}
                  end={[0, 1]}
                  style={{
                    paddingVertical: 16,
                    paddingHorizontal: 24,
                    borderRadius: 4,
                    borderWidth: 0.8,
                    borderColor: "#374151",
                    height: 69,
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 18,
                  }}
                >
                  <Text style={AppFontStyle.BOLD_ANYBODY_22}>{`£${details ? details?.totalBalance : 0}`}</Text>
                </LinearGradient>
              </View>
            )}

            {(deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM) ? (
              <View style={{ zIndex: -1, flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginTop: 15 }}>
                <View style={{ width: "40%", marginRight: 10 }}>
                  <Text
                    style={
                      isMobilePastEvent
                        ? {
                          color: "#C3C3C3",
                          fontFamily: "Barlow",
                          fontSize: 16,
                          fontWeight: "400",
                          lineHeight: 18.48,
                        }
                        : AppFontStyle.REGULAR_ANYBODY_16
                    }
                  >
                    {isMobilePastEvent
                      ? `The challenge is for ${formattedStake}`
                      : (
                        <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { color: "#9CA3AF" }]}>
                          This challenge is for {formattedStake} Belts: <Text style={[AppFontStyle.BOLD_ANYBODY_12, { color: AppColors.white }]}>worth {details?.totalCredits} credits</Text>
                        </Text>
                      )}
                  </Text>

                  {!isMobilePastEvent && (
                    <View
                      style={{
                        marginTop: 10,
                        alignItems: "flex-start",
                        zIndex: -1,
                      }}
                    >
                      <LinearGradient
                        colors={["#1F2937", "#111827"]}
                        start={[0, 0]}
                        end={[0, 1]}
                        style={{
                          paddingVertical: 16,
                          paddingHorizontal: 24,
                          borderRadius: 4,
                          borderWidth: 0.8,
                          borderColor: "#374151",
                          height: 34,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Text style={AppFontStyle.BOLD_ANYBODY_22}>{`£${details ? details?.totalBalance : 0}`}</Text>
                      </LinearGradient>
                    </View>
                  )}
                </View>

                <View style={{ width: "60%", flexDirection: "row", justifyContent: "center", alignItems: "flex-start", flexWrap: "wrap", gap: hideTimer ? 8 : 10 }}>
                  {updatedMedals.map((medal) => (
                    <View
                      key={medal.id}
                      style={{ alignItems: "center", marginRight: hideTimer ? 0 : 10 }}
                    >
                      <Image
                        source={medal.image}
                        style={{
                          width: isMobilePastEvent ? 65.469 : (hideTimer ? 65.469 : 50),
                          height: isMobilePastEvent ? 41.363 : (hideTimer ? 43 : 50),
                          resizeMode: "contain",
                          alignSelf: isMobilePastEvent ? "stretch" : undefined,
                        }}
                      />
                      <Text style={hideTimer ? { ...AppFontStyle.BOLD_ANYBODY_14 } : AppFontStyle.BOLD_ANYBODY_18}>
                        {medal.value}
                      </Text>
                      <Text style={hideTimer ? { ...AppFontStyle.REGULAR_ANYBODY_10, fontSize: 10 } : AppFontStyle.REGULAR_ANYBODY_12}>
                        {medal.name}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            ) : (
              <View style={{ marginTop: 15, alignItems: "center" }}>

                <View>
                  <Text style={[AppFontStyle.BOLD_ANYBODY_16, { marginBottom: 10, alignSelf: 'flex-start' }]}>Wager</Text>
                  <View style={{ flexDirection: "row", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                    {updatedMedals.map((medal) => (
                      <View
                        key={medal.id}
                        style={{ alignItems: "center", marginRight: 10 }}
                      >
                        <Image
                          source={medal.image}
                          style={{
                            width: 50,
                            height: 50,
                            resizeMode: "contain",
                          }}
                        />
                        <Text style={AppFontStyle.BOLD_ANYBODY_18}>
                          {medal.value}
                        </Text>
                        <Text style={AppFontStyle.REGULAR_ANYBODY_12}>
                          {medal.name}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              </View>
            )}


          </View>
        )}
      </>
    );

    return hideTimer ? (
      <View style={containerStyle}>{content}</View>
    ) : (
      <LinearGradient
        colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
        start={[0, 0]}
        end={[1, 1]}
        style={containerStyle}
      >
        {content}
      </LinearGradient>
    );
  }
);

EventDetailCard.displayName = "EventDetailCard";

export default EventDetailCard;

const useStyles = CreateResponsiveStyle(
  {
    countdownContainer: {
      backgroundColor: AppColors.black_121212,
      borderRadius: 20,
      padding: 20,
    },
    addDescriptionView: {
      width: 603,
    },
    eventDetailsContainer: {
      alignSelf: "flex-end",
      marginVertical: 36,
      flex: 1,
    },
    gameshowOuterView: {
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 12,
      paddingHorizontal: 15,
    },
    btnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 10,
      shadowOpacity: 0,
      height: 44,
      alignSelf: "center",
      margin: 10,
      borderRadius: 7,
      width: 212,
    },
    warnBtnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 10,
      backgroundColor: AppColors.red_AF0707,
      shadowOpacity: 0,
      height: 44,
      alignSelf: "center",
      borderRadius: 7,
      bottom: 90,
      position: "absolute",
      right: 450,
      top: -177,
      width: 234,
      boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
    },
    optionBtnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 10,
      backgroundColor: AppColors.appGreenColor_51AF07,
      shadowOpacity: 0,
      height: 44,
      alignSelf: "center",
      borderRadius: 7,
      bottom: 90,
      position: "absolute",
      right: 350,
      top: -177,
      width: 94,
      boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
    },
    allBtns: {
      position: "absolute",
      right: 0,
    },
    streamBtnView: {},
    deviceflexDirection: {
      flexDirection: "row",
    },
  },
  {
    [DEVICE_SIZES.MD]: {
      countdownContainer: {
        backgroundColor: AppColors.black_121212,
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
      },
      addDescriptionView: {
        width: "100%",
      },
      eventDetailsContainer: {
        alignSelf: "flex-start",
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      btnStyle: {
        shadowOpacity: 0,
        height: 44,
        alignSelf: "flex-end",
        borderRadius: 7,
        width: "40%",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      allBtns: {
        position: "static",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      warnBtnStyle: {
        top: -60,
        right: 160,
      },
      optionBtnStyle: {
        top: -60,
        right: 470,
      },
    },
    [DEVICE_SIZES.LG]: {
      countdownContainer: {
        backgroundColor: AppColors.black_121212,
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
      },
      addDescriptionView: {
        width: "100%",
      },
      eventDetailsContainer: {
        alignSelf: "center",
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      btnStyle: {
        shadowOpacity: 0,
        height: 44,
        alignSelf: "flex-end",
        borderRadius: 7,
        width: "40%",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      allBtns: {
        position: "static",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      warnBtnStyle: {
        top: -60,
        right: 160,
      },
      optionBtnStyle: {
        top: -60,
        right: 470,
      },
    },
    [DEVICE_SIZES.SM]: {
      countdownContainer: {
        backgroundColor: AppColors.black_121212,
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
      },
      addDescriptionView: {
        width: "100%",
      },
      eventDetailsContainer: {
        borderRadius: 10,
        alignSelf: "flex-end",
        marginVertical: 15,
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      btnStyle: {
        shadowOpacity: 0,
        height: 44,
        alignSelf: "flex-end",
        bottom: 10,
        borderRadius: 7,
        width: "50%",
        position: "static",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      allBtns: {
        position: "static",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      warnBtnStyle: {
        right: 20,
        top: 0,
      },
      optionBtnStyle: {
        right: -80,
        top: 0,
      },
    },
    [DEVICE_SIZES.XS]: {
      countdownContainer: {
        backgroundColor: AppColors.black_121212,
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
      },
      addDescriptionView: {
        width: "100%",
      },
      eventDetailsContainer: {
        alignSelf: "center",
        marginVertical: 15,
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      btnStyle: {
        shadowOpacity: 0,
        height: 44,
        alignSelf: "flex-end",
        bottom: 10,
        borderRadius: 7,
        width: "60%",
        position: "static",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      allBtns: {
        position: "static",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      warnBtnStyle: {
        right: 20,
        top: 0,
      },
      optionBtnStyle: {
        right: -80,
        top: 0,
      },
    },
  }
);
